import { DatePicker, Select, Space, Card, Button, message } from "antd";
import { useTranslation } from "react-i18next";
import { useGetLevelDTerms } from "@hooks";
import { LevelDReportFilter, LevelDTerm } from "../../types/leveld-reports";
import dayjs, { Dayjs } from "dayjs";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { useMemo } from "react";

const { RangePicker } = DatePicker;

interface LevelDReportFiltersProps {
  filter: LevelDReportFilter;
  onFilterChange: (filter: LevelDReportFilter) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

export const LevelDReportFilters = ({
  filter,
  onFilterChange,
  onRefresh,
  isLoading = false,
}: LevelDReportFiltersProps) => {
  const { t } = useTranslation();
  // Fetch terms within selected date range so dropdown shows relevant exam periods
  const { data: terms, isLoading: termsLoading } = useGetLevelDTerms(
    filter.startDate,
    filter.endDate
  );

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      // Validate start <= end
      if (dates[0].isAfter(dates[1])) {
        message.warning(t("levelDReports.filters.invalidDateRange"));
        return;
      }
      // Validate range is not more than 2 years
      if (dates[1].diff(dates[0], 'year', true) > 2) {
        message.warning(t("levelDReports.filters.dateRangeTooLarge"));
        return;
      }
      onFilterChange({
        ...filter,
        startDate: dates[0].startOf('day').toISOString(),
        endDate: dates[1].endOf('day').toISOString(),
        examTermId: undefined, // Reset term when dates change
      });
    }
  };

  // Disable future dates
  const disabledDate = (current: Dayjs) => {
    return current && current.isAfter(dayjs().endOf('day'));
  };

  const handleTermChange = (value: string | undefined) => {
    onFilterChange({
      ...filter,
      examTermId: value,
    });
  };

  const dateValue: [Dayjs | null, Dayjs | null] = [
    filter.startDate ? dayjs(filter.startDate) : null,
    filter.endDate ? dayjs(filter.endDate) : null,
  ];

  // Format term options with localized dates
  const termOptions = useMemo(() => {
    return [
      { value: undefined, label: t("levelDReports.filters.allTerms") },
      ...(terms?.map((term: LevelDTerm) => ({
        value: term.id,
        // Format date based on current locale
        label: `${term.examName} - ${dayjs(term.date).format('MMM D, YYYY')}`,
      })) || []),
    ];
  }, [terms, t]);

  return (
    <Card 
      size="small" 
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <FilterOutlined />
          {t("levelDReports.filters.title")}
        </Space>
      }
      extra={
        onRefresh && (
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={onRefresh}
            disabled={isLoading}
          >
            {t("levelDReports.filters.refresh")}
          </Button>
        )
      }
    >
      <Space wrap size="middle">
        <Space direction="vertical" size="small">
          <span style={{ fontSize: 12, color: "#666" }}>
            {t("levelDReports.filters.dateRange")}
          </span>
          <RangePicker
            value={dateValue}
            onChange={handleDateRangeChange}
            format="DD.MM.YYYY"
            allowClear={false}
            disabled={isLoading}
            disabledDate={disabledDate}
            style={{ minWidth: 280 }}
          />
        </Space>

        <Space direction="vertical" size="small">
          <span style={{ fontSize: 12, color: "#666" }}>
            {t("levelDReports.filters.term")}
          </span>
          <Select
            value={filter.examTermId}
            onChange={handleTermChange}
            placeholder={t("levelDReports.filters.allTerms")}
            allowClear
            loading={termsLoading}
            disabled={isLoading || termsLoading}
            style={{ minWidth: 300 }}
            options={termOptions}
          />
        </Space>
      </Space>
    </Card>
  );
};
